import React from 'react';
import Button, { ButtonGroup } from '@atlaskit/button';
import { Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useAIAnalyticsContext } from '@atlassian/jira-instrumentation-common/src/ai-analytics-context/context.tsx';
import { AI_EVENTS } from '@atlassian/jira-instrumentation-common/src/ai-analytics-context/common.tsx';
import { OPEN, RESOLVED, FEEDBACK_BUTTONS } from '../../common/constants.tsx';
import { useRelatedIssuesForIssue } from '../../services/context.tsx';
import { useRelatedIssuesFeedbackCollector } from '../../services/use-feedback-collector/index.tsx';
import messages from './messages.tsx';

export const TEST_ID_BASE = 'smart-related-issues-panel.ui.feedback-sentiment.smart';

export const RelatedIssuesFeedbackSentiment = ({
	sessionId,
	hasClickedSentiment,
	setHasClickedSentiment,
	openIssuesDistance,
	resolvedIssuesDistance,
	openIssuesCrossEncoderScore,
	resolvedIssuesCrossEncoderScore,
}: {
	sessionId: string;
	hasClickedSentiment: boolean;
	setHasClickedSentiment: React.Dispatch<React.SetStateAction<boolean>>;
	openIssuesDistance: Array<number>;
	resolvedIssuesDistance: Array<number>;
	openIssuesCrossEncoderScore: Array<number>;
	resolvedIssuesCrossEncoderScore: Array<number>;
}) => {
	const [{ isLoading, error, data }] = useRelatedIssuesForIssue();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { openRelatedIssuesFeedback } = useRelatedIssuesFeedbackCollector(sessionId);
	const { formatMessage } = useIntl();
	const { fireTrackAI } = useAIAnalyticsContext();

	// Only show sentiment buttons when the results have loaded without error AND there are >0 results.
	const totalRelatedIssueCount: number = (data[OPEN]?.length ?? 0) + (data[RESOLVED]?.length ?? 0);
	if (isLoading || error || totalRelatedIssueCount === 0) {
		return null;
	}

	if (hasClickedSentiment) {
		return (
			<Flex gap="space.050" alignItems="center" xcss={RelatedIssuesFeedbackSentimentStyles}>
				{formatMessage(messages.thanksForYourFeedback)}{' '}
				{formatMessage(messages.toImproveFutureResultsWithPlaceholder, {
					tellUsMore: (
						<Button
							appearance="link"
							spacing="none"
							data-testId="smart-related-issues-panel.ui.feedback-sentiment.smart.feedback-collector"
							onClick={openRelatedIssuesFeedback}
							interactionName="auto-generated.jira-smart-related-issues-panel.ui.feedback-sentiment.button"
						>
							{formatMessage(messages.tellUsMore)}
						</Button>
					),
				})}
			</Flex>
		);
	}

	const onButtonClick = (
		wereResultsRelevant: boolean,
		openIssuesDistances: Array<number>,
		resolvedIssuesDistances: Array<number>,
		openIssuesCrossEncoderScores: Array<number>,
		resolvedIssuesCrossEncoderScores: Array<number>,
	) => {
		setHasClickedSentiment(true);
		fireTrackAI(AI_EVENTS.FEEDBACK, {
			aiFeedbackResult: wereResultsRelevant ? 'up' : 'down',
			totalRelatedIssueCount,
		});
		fireUIAnalytics(createAnalyticsEvent({}), 'relatedIssuesSentiment clicked', {
			wereResultsRelevant,
			openIssuesDistances,
			resolvedIssuesDistances,
			openIssuesCrossEncoderScores,
			resolvedIssuesCrossEncoderScores,
		});
	};

	return (
		<Flex gap="space.050" alignItems="center" xcss={RelatedIssuesFeedbackSentimentStyles}>
			<Text as="p">{formatMessage(messages.areTheseRelevant)}</Text>
			<ButtonGroup>
				{([FEEDBACK_BUTTONS.YES, FEEDBACK_BUTTONS.NO] as const).map((buttonId) => (
					<Button
						key={buttonId}
						spacing="compact"
						onClick={() =>
							onButtonClick(
								buttonId === FEEDBACK_BUTTONS.YES,
								openIssuesDistance,
								resolvedIssuesDistance,
								openIssuesCrossEncoderScore,
								resolvedIssuesCrossEncoderScore,
							)
						}
						data-testId={`${TEST_ID_BASE}.${buttonId}`}
						aria-label={formatMessage(messages[`${buttonId}AriaLabel`])}
						interactionName="auto-generated.jira-smart-related-issues-panel.ui.feedback-sentiment.button1"
					>
						{formatMessage(messages[buttonId])}
					</Button>
				))}
			</ButtonGroup>
		</Flex>
	);
};

const RelatedIssuesFeedbackSentimentStyles = xcss({
	minHeight: token('space.300'),
});
